import { MeetingData, MeetingDataDto, PendingMeetingData } from '@/components/meet/MeetingData';
import { ActivityFilter, FiltersResponse } from '@/models/Filter';
import { ActivityKind } from '@/modules/activities/enums/ActivityKind';
import { clone } from '@/services/utils/BasicUtils';
import { FpAxios } from 'fp-components';
import { sortBy } from 'lodash-es';
import Activity, { ActivityDTO, ActivityRequest } from '@/models/Activity';
import {ActivityStatus, ActivityTookPlaceStatus} from '@/modules/activities/enums/ActivityStatus';

class ActivityApi {
  private activityAxios = FpAxios.create();
  readonly baseUrl = 'activities';

  async getAllActivities(): Promise<Array<Activity>> {
    const response = await this.activityAxios.get<Array<ActivityDTO>>(this.baseUrl);
    return response.data.map((activityDTO: ActivityDTO) => new Activity(activityDTO)) || [];
  }

  async getAllUsersActivities(): Promise<Array<Activity>> {
    const response = await this.activityAxios.get<Array<ActivityDTO>>(`${this.baseUrl}/users`);
    return response.data.map((activityDTO: ActivityDTO) => new Activity(activityDTO)) || [];
  }

  async getActivities(dealId?: number): Promise<Array<Activity>> {
    const url = dealId !== undefined
      ? `${this.baseUrl}/deal/${dealId}`
      : `${this.baseUrl}`;
    const response = await this.activityAxios.get<Array<ActivityDTO>>(url);
    return sortBy(response.data.map((activityDTO: ActivityDTO) => new Activity(activityDTO)), ['startDate',]);
  }

  async getClientActivities(clientId?: number): Promise<Array<Activity>> {
    const response = await this.activityAxios.get<Array<ActivityDTO>>(`${this.baseUrl}/client/${clientId}`);
    return sortBy(response.data.map((activityDTO: ActivityDTO) => new Activity(activityDTO)), ['startDate',]);
  }

  async getExpiredActivities(): Promise<Array<Activity>> {
    const response = await this.activityAxios.get<Array<ActivityDTO>>(`${this.baseUrl}/expired`);
    return response.data.map((activityDTO: ActivityDTO) => new Activity(activityDTO));
  }

  async getActivitiesForUser(userId?: number): Promise<Array<Activity>> {
    const url = `${this.baseUrl}/user/${userId}`;
    const response = await this.activityAxios.get<Array<ActivityDTO>>(url);
    return response.data.map((activityDTO: ActivityDTO) => new Activity(activityDTO));
  }

  async addActivity(activity: Activity): Promise<Activity> {
    if (activity.isOther && !activity.endDate) {
      activity.endDate = activity.startDate;
    }
    const data: PartialDeep<Activity> = clone(activity);
    if (data.client) {
      delete data.client;
    }
    const response = await this.activityAxios.post<ActivityDTO>(`${this.baseUrl}`, data);
    if (activity.attachment) {
      try {
        await this.addAttachmentsToActivity(response.data.id!, activity.attachment);
      } catch (e) {
        console.error(e);
      }
    }
    return new Activity(response.data);
  }

  async addAttachmentsToActivity(ids: number | number[], attachment: File): Promise<void> {
    const form = new FormData();
    form.append('activitiesList', `[${Array.isArray(ids) ? ids.join(', ') : `${ids}`}]`);
    form.append('activityAttachmentFile', attachment);
    await this.activityAxios.post(`${this.baseUrl}/attachment-upload`, form);
  }

  async editActivity(activity: Activity): Promise<Activity> {
    if (![ActivityKind.MEETING, ActivityKind.PHONE,].includes(activity.kind)) {
      activity.requireReporting = false;
    }

    const response = await this.activityAxios.put<Activity>(`${this.baseUrl}/${activity.id}`, activity);
    return new Activity(response.data);
  }

  async deleteActivity(activityId: number): Promise<boolean> {
    await this.activityAxios.delete(`${this.baseUrl}/${activityId}`);
    return false;
  }

  /** @Warning inside the function status maps to -> url endpoint, tookPlaceStatus maps to -> query param */
  async changeActivityDoneStatus(activityId: number, status: ActivityStatus, tookPlaceStatus?: ActivityTookPlaceStatus): Promise<void> {
    const tookPlaceStatusAsStatus = tookPlaceStatus ? `?status=${tookPlaceStatus}` : '';
    await this.activityAxios.post(`${this.baseUrl}/${activityId}/${status}${tookPlaceStatusAsStatus}`);
  }

  async filterActivities(filters?: ActivityFilter): Promise<FiltersResponse<Activity>> {
    const response = await this.activityAxios.post<FiltersResponse<Activity>>(`${this.baseUrl}/search`, filters);
    response.data.items = response.data.items.map((activityDTO: ActivityDTO) => new Activity(activityDTO));
    return response.data;
  }

  async getMeetingData(uuid: string): Promise<MeetingData> {
    let response;
    try {
      response = await this.activityAxios.get<MeetingDataDto>(`/${this.baseUrl}/teleconference/auth/${uuid}`);
    } catch (e) {
      console.error('getMeetingData', e);
    }

    return new MeetingData(response.data);
  }

  async resendClientMeetingLink(activityId: number): Promise<void> {
    await this.activityAxios.put<FiltersResponse<Activity>>(`${this.baseUrl}/${activityId}/resend-client`,);
  }

  async validateActivity(activity: ActivityRequest): Promise<boolean> {
    try {
      const response = await this.activityAxios.post(`${this.baseUrl}/validate`, activity);
      return true;
    } catch (e) {
      console.error('error while validate activity', e);
      return false;
    }
  }
}

export default new ActivityApi();
